import { BrokenInAppBrowser } from "./detect";
import { EscapeScheme } from "./escapeAttempts";

const ESCAPE_REPORT_ENDPOINT = "/api/link/escape";

export type EscapeResult =
  | { outcome: "started"; attempts: number }
  /** A scheme took the visitor out of the webview. */
  | { outcome: "left"; scheme: EscapeScheme; attemptIndex: number }
  /** Every scheme was swallowed and the page is still here. */
  | { outcome: "exhausted" };

export type EscapeTrigger = "auto" | "tap" | "appPreview";

/**
 * The click an escape reports against when its caller has none. Store buttons
 * know nothing about clicks, so without this every escape fired from a tap
 * would go unmeasured.
 *
 * Safe as module state because only the browser imports this file.
 */
let pageClickId: string | null = null;

export function rememberEscapeClickId(clickId: string | null): void {
  if (clickId) pageClickId = clickId;
}

// One start and one outcome per trigger, not per page load. A tap is a second
// escape over the same click and fires schemes the automatic pass never can, so
// collapsing the two would measure only the pass that was always going to run
// first. The server keys them apart for the same reason.
const reportedStarts = new Set<EscapeTrigger>();
const reportedOutcomes = new Set<EscapeTrigger>();

/**
 * Records how an escape went, so the scheme order in escapeAttempts.ts comes
 * from production data rather than guesses.
 *
 * Two events, because the ending alone cannot be read. An escape that works
 * takes the page away before it can report, and "left" then goes out over
 * sendBeacon, which is lossy exactly when it matters. So the chain also says
 * when it starts, over an ordinary request while the page is still here:
 *
 *   started, no outcome  = the chain left, which is what success looks like
 *   started + exhausted  = every scheme was refused
 *   neither              = no escape ran
 *
 * Read that per trigger: a tap after an exhausted automatic pass is its own
 * pair, under its own metadata keys.
 *
 * Nothing is awaited and every failure is swallowed: telemetry must never
 * delay or break an escape.
 */
export function reportEscapeOutcome({
  clickId,
  browser,
  trigger,
  result,
  isLeaving,
}: {
  clickId: string | null | undefined;
  browser: BrokenInAppBrowser;
  trigger: EscapeTrigger;
  result: EscapeResult;
  /** True when called from the handler that fired as the page went away. */
  isLeaving: boolean;
}): void {
  const reportedClickId = clickId ?? pageClickId;
  if (!reportedClickId) return;

  const reported =
    result.outcome === "started" ? reportedStarts : reportedOutcomes;
  if (reported.has(trigger)) return;
  reported.add(trigger);

  const body = JSON.stringify({
    clickId: reportedClickId,
    browser,
    trigger,
    ...result,
  });

  try {
    // Another app is taking over, so a normal fetch may never get flushed.
    if (isLeaving && typeof navigator.sendBeacon === "function") {
      navigator.sendBeacon(
        ESCAPE_REPORT_ENDPOINT,
        new Blob([body], { type: "application/json" })
      );
      return;
    }

    // The catch keeps a failed report out of the visitor's console.
    fetch(ESCAPE_REPORT_ENDPOINT, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body,
      keepalive: true,
    }).catch(() => {});
  } catch {
    // A blocked Blob or a webview with no fetch is not worth failing over.
  }
}
